import { DefaultChatTransport, type UIMessage } from 'ai';
import { agentStreamHeaders } from '@/lib/api/agent-stream-headers';
import { fetchWithAuth } from '@/lib/api/fetch-with-auth';
import { withAgentUnavailableDetection } from '@/shared/lib/ai-stream';

export interface RelayAgentTransportOptions {
  /** The service hosting the agent, which mounts Relay's routes. */
  baseUrl: string;
  agentId: string;
  contextId: string;
  workspaceId: string;
  /** The agent reads PDF documents given in the chat itself, rather than through its tools. */
  documents?: boolean;
}

const PDF = 'application/pdf';

interface MessageFile {
  assetId: string;
  mimeType: string;
  metadata?: { fileName?: string };
}

function filesOf(message: UIMessage): MessageFile[] {
  const files = (message.metadata as { files?: MessageFile[] } | undefined)?.files;
  return Array.isArray(files) ? files : [];
}

/**
 * Names each file a person's message carries, with its id, in what is sent: an agent's tools open
 * a file by id, and the file is otherwise only a chip drawn in the bubble.
 */
function withFilesNamed(message: UIMessage): UIMessage {
  const files = filesOf(message);
  if (message.role !== 'user' || files.length === 0) return message;
  const named = files
    .map(
      (f) =>
        // eslint-disable-next-line i18next-templates/no-literal-string -- read by the agent, never shown
        `Attached file: ${f.metadata?.fileName ?? f.assetId} (id ${f.assetId}, ${f.mimeType})`,
    )
    .join('\n');
  return { ...message, parts: [...message.parts, { type: 'text', text: named }] };
}

/**
 * Every document given in the conversation, sent with every turn: the service keeps no
 * conversation, so a file sent once would be gone the turn after.
 */
function documentsIn(messages: readonly UIMessage[]): RelayAttachment[] {
  const byId = new Map<string, RelayAttachment>();
  for (const message of messages) {
    for (const file of filesOf(message)) {
      if (file.mimeType !== PDF) continue;
      byId.set(file.assetId, {
        mediaType: PDF,
        kind: 'asset',
        id: file.assetId,
        ...(file.metadata?.fileName ? { filename: file.metadata.fileName } : {}),
      });
    }
  }
  return Array.from(byId.values());
}

/** A file another service stores, by reference; the hosting service resolves it. */
export interface RelayAttachment {
  mediaType: string;
  kind: 'asset';
  id: string;
  filename?: string;
}

/** How the chat answers a paused turn; the same shape the reasoner takes as `approvals`. */
interface ApprovalAnswer {
  approvalId: string;
  approved: boolean;
  reason?: string;
  answers?: unknown;
}

interface ToolCallPart {
  type: string;
  toolCallId?: string;
  state?: string;
  approval?: { id: string };
}

/**
 * Writes each answer onto the call it answers, as the AI SDK's own approval response does, so the
 * engine finds the decision in the resent conversation. A question's answers travel beside it as
 * `humanInputs`, keyed by the call that asked.
 */
function answerPausedCalls(messages: readonly UIMessage[], approvals: readonly ApprovalAnswer[]) {
  const byId = new Map(approvals.map((approval) => [approval.approvalId, approval]));
  const humanInputs: Record<string, unknown> = {};
  const answered = messages.map((message) => ({
    ...message,
    parts: message.parts.map((part) => {
      const call = part as ToolCallPart;
      const answer = call.approval ? byId.get(call.approval.id) : undefined;
      if (!answer || call.state !== 'approval-requested') return part;
      if (answer.approved && answer.answers && call.toolCallId) {
        humanInputs[call.toolCallId] = answer.answers;
      }
      return {
        ...part,
        state: 'approval-responded',
        approval: {
          id: answer.approvalId,
          approved: answer.approved,
          ...(answer.reason ? { reason: answer.reason } : {}),
        },
      } as UIMessage['parts'][number];
    }),
  }));
  return { answered, humanInputs };
}

/**
 * The transport for an agent another service hosts through Relay's routes. That service keeps no
 * conversation, so every turn resends it whole, under `contextId`, and a paused turn resumes by
 * resending it with the paused calls answered.
 */
export function createRelayAgentTransport({
  baseUrl,
  agentId,
  contextId,
  workspaceId,
  documents,
}: RelayAgentTransportOptions) {
  return new DefaultChatTransport<UIMessage>({
    api: `${baseUrl}/api/v1/agents/${agentId}/stream`,
    credentials: 'include',
    fetch: withAgentUnavailableDetection(fetchWithAuth),
    prepareSendMessagesRequest: (opts) => {
      const sent = (opts.body ?? {}) as { approvals?: ApprovalAnswer[] };
      // Data parts are drawn on screen and never read by the model, so resending them each turn
      // would only grow the request.
      const withoutData = opts.messages.map((message) =>
        withFilesNamed({
          ...message,
          parts: message.parts.filter((part) => !part.type.startsWith('data-')),
        }),
      );
      const { answered, humanInputs } = answerPausedCalls(withoutData, sent.approvals ?? []);
      const attachments = documents ? documentsIn(opts.messages) : [];
      return {
        api: opts.api,
        credentials: opts.credentials,
        body: {
          contextId,
          uiMessages: answered,
          ...(Object.keys(humanInputs).length > 0 ? { humanInputs } : {}),
          ...(attachments.length > 0 ? { attachments } : {}),
        },
        headers: agentStreamHeaders(opts.headers, workspaceId),
      };
    },
  });
}
